import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';

import ActionButtons from './ActionButtons';
import ActionStepSparkle from 'components/_shared/ActionStepSparkle';
import Card from '../../components/Card';
import LoadingCard from '../../components/LoadingCard';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    actionStepCard: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    title: {
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: '25px'
    },
    description: {
        fontSize: '16px',
        fontWeight: 300,
        lineHeight: '21px'
    },
    bonus: {
        backgroundColor: '#E3E3FA',
        borderRadius: '20px',
        fontSize: '16px',
        fontWeight: 800,
        lineHeight: '21px',
        padding: '6px 16px',
        width: 'fit-content'
    },
    classification: {
        backgroundColor: '#E9F0FB',
        borderRadius: '20px',
        border: '1px solid #1B4D9750',
        fontSize: '16px',
        color: '#1B4D97',
        fontWeight: 800,
        lineHeight: '21px',
        padding: '6px 16px',
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '& i': {
            fontSize: '14px',
            fontWeight: 500
        }
    },
    markdownTitle: {
        '& p': {
            margin: '0'
        }
    },
    actionStepsBottom: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '16px',
        justifyContent: 'space-between',
        '@media (max-width: 768px)': {
            flexDirection: 'column',
            gap: '8px'
        }
    },
    leftTags: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
    }
}));

type ActionCardProps = {
    action: any;
    isLast: boolean;
    name: string;
    setActionStepID: (id: number) => void;
    setCompleteActionStep: (complete: boolean) => void;
    setEdit: (edit: boolean) => void;
    setHasQOLMeasures: (hasQOLMeasures: boolean) => void;
    setReminder: (reminder: boolean) => void;
    setShowToast: (value: boolean) => void;
    setToastMessage: (value: string) => void;
    touchpointQuestionQOLs: any;
    updatedActionIDs: number[];
    user: any;
};

const ActionCard: React.FC<ActionCardProps> = ({
    action,
    isLast,
    name,
    setActionStepID,
    setCompleteActionStep,
    setEdit,
    setHasQOLMeasures,
    setReminder,
    setShowToast,
    setToastMessage,
    touchpointQuestionQOLs,
    updatedActionIDs,
    user
}) => {
    const classes = useStyles();

    const {
        description,
        due_at,
        goal,
        id,
        match_value,
        quality_of_life,
        requestState,
        status,
        classification
    } = action;
    const isUpdated = updatedActionIDs.includes(id);
    //only show tag if user is admin and action step is gap in care
    const showGapInCareTag = classification === 'gap_in_care' && user.is_admin;
    // hide edit button for non-admins when classification is gap in care
    const hideEdit = (classification === 'gap_in_care' && !user.is_admin) || action.action_type === 'lifecarable';

    useEffect(() => {
        if (isUpdated && status === 'completed') {
            setShowToast(true);

            setToastMessage(
                match_value
                    ? `$${match_value} bonus added to ${name}'s Funds`
                    : 'Action Step Completed'
            );

            const timeoutId = setTimeout(() => {
                setShowToast(false);
            }, 5000);

            return () => clearTimeout(timeoutId);
        }
    }, [isUpdated, match_value, name, setShowToast, setToastMessage, status]);

    return (
        <>
            {requestState === 'loading' ? (
                <LoadingCard />
            ) : (
                <Card
                    id="action-card"
                    backgroundColor={isUpdated ? '#FBFBFF' : ''}
                >
                    <div className={classes.actionStepCard}>
                        {isLast && <ActionStepSparkle />}
                        <ReactMarkdown
                            className={`${classes.markdownTitle} ${classes.title}`}
                            children={description}
                        />
                        <div className={classes.description}>
                            {quality_of_life && quality_of_life.description
                                ? quality_of_life.description
                                : goal.description}
                        </div>
                    </div>
                    <div
                        className={classes.actionStepsBottom}
                        style={isUpdated ? { justifyContent: 'flex-end' } : {}}
                    >
                        {!isUpdated && (
                            <div className={classes.leftTags}>
                                <div className={classes.bonus}>
                                    {match_value ? `$${match_value}` : `No Bonus`}
                                </div>
                                {showGapInCareTag && (
                                    <div className={classes.classification}>
                                        <i className="fas fa-notes-medical"></i> GAP IN CARE
                                    </div>
                                )}
                            </div>
                        )}
                        <ActionButtons
                            action={action}
                            date={due_at}
                            setActionStepID={setActionStepID}
                            setCompleteActionStep={setCompleteActionStep}
                            setEdit={setEdit}
                            setHasQOLMeasures={setHasQOLMeasures}
                            setReminder={setReminder}
                            touchpointQuestionQOLs={touchpointQuestionQOLs}
                            updatedActionIDs={updatedActionIDs}
                            showEdit={!hideEdit}
                        />
                    </div>
                </Card>
            )}
        </>
    );
};

export default ActionCard;
